import { CheckBoxWidget, ListItem, TabStyles } from '@tableau/widgets';
import { DataGrid } from '@tableau/data-grid-1-wrapper';
import * as React from 'react';

import { Messages } from '../../codegen/Localize';
import { CommonDataGridColumns } from '../../common/react/CommonDataGridColumns';
import { DataGridCells } from '../../common/react/components/dataGrid/DataGridCells';
import { SortableHeader } from '../../common/react/components/dataGrid/SortableHeader';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { FlowOutputStepInfo } from '../../ts/models/FlowOutputStepInfo';
import {
  RunFlowSubMenuItem,
  writeOptionsFull,
  writeOptionsIncremental,
  writeOptionsRunAllDefault,
  writeOptionsRunAllFull,
  writeOptionsRunAllIncremental
} from './RunFlowSubMenuItem';

export module FlowOutputStepGrid {

  export function columns(
    isCheckboxHeaderChecked: boolean,
    onCheckboxHeaderClicked: () => void,
    onCheckboxClicked: (row: DataGrid.Row) => void,
    onIsIncrementalChange: (row: DataGrid.Row, isIncremental: boolean) => void,
    outputsSelected: { readonly [key: string]: boolean },
    outputsIsIncremental: { readonly [key: string]: boolean },
    isIncrementalEnabled: boolean,
    isRunAllIncremental: boolean,
    selectAll: boolean,
  ): DataGrid.Column[] {
    const columns: DataGrid.Column[] = [];

    const isSelected = (rowKey: string) => {
      return outputsSelected[rowKey];
    };

    const isDisabled = selectAll;

    columns.push({
      name: 'output-steps',
      dataKey: 'flowOutputSteps',
      headerText: Messages.outputStepsColumnName(),
      sortable: false,
      headerFormatter: selectAll ?
        SortableHeader.Formatter() :
        DataGridCells.CheckboxHeaderWithText(isCheckboxHeaderChecked, onCheckboxHeaderClicked, isDisabled),
      cellFormatter: selectAll ?
        DataGridCells.PlainTextCell('name')
        : DataGridCells.CheckboxCellWithText('name', isSelected, onCheckboxClicked, isDisabled),
      growToFit:true,
      preferredWidth: 300
    });

    columns.push({
      name: 'output-names-col',
      dataKey: 'flowOutputStepsName',
      headerText: Messages.flowOverviewOutputNameColumnName(),
      sortable: false,
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: DataGridCells.PlainTextCell('datasourceName')
    });

    columns.push({
      name: 'output-types-col',
      dataKey: 'flowOutputStepsType',
      headerText: Messages.flowScheduledTaskLocation(),
      sortable: false,
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: DataGridCells.PlainTextCellWithTransform('dataConnection',
      (dataConnection) => dataConnection.connectionTypeDisplayName)
    });

    const getIsIncremental = (rowKey: string) => {
      if (selectAll)
        return String(isRunAllIncremental ? true : null);
      else
        return String(outputsIsIncremental[rowKey] || false);
    };

    const onSelectChange = (row: DataGrid.Row, isIncremental: string) => {
      onIsIncrementalChange(row, isIncremental === String(null) ? null : isIncremental === String(true));
    };

    let getMenuItems: (row: DataGrid.Row) => ListItem[];

    if (!selectAll)
      getMenuItems = getRefreshMenuItemsForSingleOutput;
    else if (isIncrementalEnabled)
      getMenuItems = getRefreshMenuItemsForRunAll;
    else
      getMenuItems = getRefreshMenuItemsForRunFull;

    if (FeatureFlags.incrementalRefresh) {
      columns.push({
        name: 'refresh-types-col',
        dataKey: 'flowOutputStepsType',
        headerText: Messages.writeOptionsRefreshType(),
        sortable: false,
        headerFormatter: SortableHeader.Formatter(),
        cellStyle: { padding: '0', marginTop: '-1px' },
        cellFormatter: DataGridCells.ListCell('refresh', getIsIncremental, onSelectChange, getMenuItems, getDisplayText, selectAll),
        growToFit: true,
        preferredWidth: 300
      });
    }

    return columns;
  }

  function getRefreshMenuItemsForRunFull(){
    return [{
      value: String(null),
      content: writeOptionsRunAllFull(false),
    }];
  }

  function getRefreshMenuItemsForRunAll(){
    return [{
      value: String(null),
      content: writeOptionsRunAllFull(false),
    }, {
      value: String(true),
      content: writeOptionsRunAllIncremental(),
    }];
  }

  function getRefreshMenuItemsForSingleOutput(row:  DataGrid.Row) {
    const output = row.data as FlowOutputStepInfo;

    if (output.isIncrementalEnabled) {
      return [
        {
          value: String(false),
          content: writeOptionsFull(output, false),
        }, {
          value: String(true),
          content: writeOptionsIncremental(output),
        }];
    }
    else {
      return [
        {
          value: String(false),
          content: writeOptionsFull(output, false),
        }];
    }
  }

  function getDisplayText(item: ListItem){
    return (item.content as RunFlowSubMenuItem).props.displayText;
  }
}
